import Modal from "../../ui-components/Modal";
import {useIntl, FormattedMessage} from "react-intl";
import Input from "../../ui-components/Input";
import {useState, useContext} from "react";
import {ArrowUpTrayIcon, DocumentTextIcon} from "@heroicons/react/24/outline";
import {TrashIcon} from "@heroicons/react/24/outline";
import {ArrowUturnLeftIcon} from "@heroicons/react/24/outline";
import Spinner from "../../ui-components/Spinner";
import Button from "../../ui-components/Button";
import {maxSize} from "./utils/constants";
import RadioGroup from "../../ui-components/RadioGroup";
import Checkbox from "../../ui-components/Checkbox";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import CountryContext from "../../CountryContext";
import {uploadFileToS3, formatBytes, ACCEPTED_FORMATS} from "./utils/FilesAndS3Utils";
import {NotificationsContext} from "../../ui-components/Notifications";
import VisibilityEditor from "@pages/WorksitesDocuments/VisibilityEditor";
const EditGroupModal = ({ group, onClose, worksiteUsers, setUserLoadedDocuments, setShowEditGroup , sharing}) => {
	const { country } = useContext(CountryContext);
	const intl = useIntl();
	const countryApiRequest = useCountryAxiosInstance(country?.code);
	const [groupName, setGroupName] = useState(group.name);
	const [documentsToDelete, setDocumentsToDelete] = useState([]);
	const [loading, setLoading] = useState(group.documents.map(() => false));
	const [filesToAdd, setFilesToAdd] = useState([]);
	const [visibilityMangment, setVisibilityManagement] = useState(
		group.flg_public || group.new ? "public" : "manage_visibility"
	);
	const [visibility, setVisibility] = useState(group.lst_visibility);
	const { push } = useContext(NotificationsContext);
	const [submitting, setSubmitting] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [askForConfirmation, setAskForConfirmation] = useState(false);


	const refetchDocuments = async () => {
		const userLoadedDocuments = await countryApiRequest.get(`/worksite_documents/${group.id_worksite}`);
		setUserLoadedDocuments(userLoadedDocuments);
	}

	const uploadDocuments = async (groupId) => {
		const filesUploaded = await Promise.all(filesToAdd.map(async (file) => {
			try {
				const url = await uploadFileToS3(
					countryApiRequest,
					file,
					`documents/${country.code}/${group.id_worksite}`,
					'documents',
				);
				return {
					name: file.name,
					size: file.size,
					s3_url: url,
				}
			} catch (e) {
				push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
			}
		}));
		// add files calling post to backend
		for (let file of filesUploaded.filter(f => f)) {
			await countryApiRequest.post(`/worksite_documents/document`, {
				id_group: groupId,
				name: file.name,
				s3_url: file.s3_url,
				val_size: file.size,
				lst_visibility: [],
				flg_public: true,
			})
		}
	}

	const deleteDocuments = async () => {
		await Promise.all(documentsToDelete.map(async (document) => {
			await countryApiRequest.delete(`/worksite_documents/document/${document.id}`);
		}));
	}

	return <Modal
		title={intl.formatMessage({id: "edit_group"})}
		onExit={onClose}
		>
		<div className="justify-center m-4 grid grid-cols-1 gap-y-4">
			<h2 className="text-3xl mb-4">
				<FormattedMessage id={group.new ? "create_group" : "edit_group"}/>
			</h2>
			<Input
				label={intl.formatMessage({id: "group_name"})}
				value={groupName}
				onChange={(e) => {
					setGroupName(e.target.value)
				}}
				showErrorDialog={false}
				hideBottomMargin={true}
			/>
			{
				/*FILES ALREADY IN THE GROUP*/
				group.documents.length > 0 && <div className={"grid grid-cols-1 gap-y-2 mb-4"}>
					{group.documents.map((document, index) => {
						return <div key={index} className={"grid grid-cols-6 items-center"}>
							<div className={"col-span-4 gap-x-4 items-center grid grid-cols-8"}>
								{loading[index] ?
									<div className={"relative h-6 w-6"}>
										<Spinner styleType={"white"}/>
									</div>
									: <DocumentTextIcon className={"h-6 w-6"}/>
								}
								<span
									className={`col-span-7 underline cursor-pointer ${documentsToDelete.includes(document) ? "line-through" : ""}`}
									onClick={async () => {
										setLoading(loading.map((l, i) => i === index ? true : l))
										try {
											await document.onDocumentClicked()
										} catch (e) {
											push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
										}
										setLoading(loading.map((l, i) => i === index ? false : l))
									}}>
								{document.name}
							</span>
							</div>
							<span className={`${documentsToDelete.includes(document) ? "line-through" : ""}`}>
							{formatBytes(document.val_size)}
						</span>
							{documentsToDelete.includes(document) ?
								<ArrowUturnLeftIcon
									className={"h-6 w-6 cursor-pointer"}
									onClick={() => {
										setDocumentsToDelete(documentsToDelete.filter(d => d !== document))
									}}
								/> :
								<TrashIcon
									className={"h-6 w-6 cursor-pointer"}
									onClick={() => {
										setDocumentsToDelete([...documentsToDelete, document])
									}}
								/>
							}
						</div>
					})}
				</div>
			}
			{
				/*FILES TO ADD (THAT THE USER LOADED)*/
				filesToAdd.length > 0 && <div className={"grid grid-cols-1 gap-y-4 items-center"}>
					{filesToAdd.map((file, index) => {
						return <div key={index} className={"grid grid-cols-6 items-center"}>
							<div className={"flex col-span-4 items-center grid grid-cols-8"}>
								<DocumentTextIcon className={"h-6 w-6"}/>
								<span
									className={`col-span-7`}>
										{file.name}
									</span>
							</div>
							<span className={`justify-end ${documentsToDelete.includes(file) ? "line-through" : ""}`}>
									{formatBytes(file.size)}
								</span>
							<TrashIcon
								className={"justify-end h-6 w-6 cursor-pointer"}
								onClick={() => {
									setFilesToAdd(filesToAdd.filter(f => f !== file))
								}}
							/>
						</div>
					})}
				</div>
			}
			<div>
				<Button
					full
					className={"w-full"}
					styleType={"white"}
					onClick={() => {
						document.getElementById("file-input").click();
					}}
					Icon={ArrowUpTrayIcon}
				>
					<input
						type="file"
						multiple={true}
						id="file-input"
						className="hidden"
						accept={ACCEPTED_FORMATS.join(", ")}
						onChange={(e) => {
							let newList = [];
							let size = 0;
							Object.keys(e.target.files).forEach((key) => {
								newList.push(e.target.files[key]);
								size += e.target.files[key].size;
							});
							if (size > maxSize) {
								push({type: 'error', title: intl.formatMessage({id: 'size_limit_exceeded'})});
							} else {
								setFilesToAdd([...filesToAdd, ...newList])
							}
						}}
					/>
					{intl.formatMessage({id: "add_documents"})}
				</Button>
			</div>
			<VisibilityEditor
				labelKey="group_visibility"
				publicLabelKey="visibile_all_worksite_users"
				choice={visibilityMangment}
				setChoice={setVisibilityManagement}
				users={worksiteUsers}
				visibility={visibility}
				setVisibility={setVisibility}
				sharing={sharing}
			/>
			<div>
				<Button
					full
					styleType={"primary"}
					disabled={groupName === ""
						|| groupName === null
						|| groupName === undefined}
					submitting={submitting}
					onClick={async () => {
						setSubmitting(true)
						if (group.new) {
							// create group
							const newGroup = await countryApiRequest.post(`/worksite_documents/group`, {
								name: groupName,
								flg_public: visibilityMangment === "public",
								lst_visibility: visibilityMangment === "manage_visibility" ? visibility : [],
								id_worksite: group.id_worksite,
							});
							// upload files to s3 and documents to backend
							await uploadDocuments(newGroup.id);
						} else {
							// edit group
							await countryApiRequest.put(`/worksite_documents/group/${group.id}`, {
								name: groupName,
								flg_public: visibilityMangment === "public",
								lst_visibility: visibilityMangment === "manage_visibility" ? visibility : [],
								val_order: group.val_order,
							});
							// upload files to s3 and documents to backend
							await uploadDocuments(group.id);
							// delete documents
							await deleteDocuments()
						}
						await refetchDocuments();
						setSubmitting(false)
						onClose();
					}}>
					{intl.formatMessage({id: "confirm"})}
				</Button>
			</div>
			{!group.new && <div>
				{!askForConfirmation ?
					<Button
						full
						styleType={"redWhite"}
						submitting={deleting}
						onClick={() => {
							setAskForConfirmation(true)
						}}
					>
						{intl.formatMessage({id: "delete"})}
					</Button> :
					!deleting ?
						<div className={"grid grid-cols-2 gap-x-4"}>
							<Button
								full
								styleType={"redWhite"}
								onClick={async () => {
									await countryApiRequest.delete(`/worksite_documents/group/${group.id}`);
									await refetchDocuments()
									setDeleting(false)
									onClose()
								}}
							>
								{intl.formatMessage({id: "confirm"})}
							</Button>
							<Button
								full
								styleType={"white"}
								onClick={() => {
									setAskForConfirmation(false)
								}}
							>
								{intl.formatMessage({id: "cancel"})}
							</Button>
						</div> :
						<Spinner styleType={"white"}/>}
			</div>
			}
		</div>
	</Modal>
}

export default EditGroupModal;